import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { googleMapUrl } from '../../../../utils/constants';
import GoogleMapContainer from './css';
import mapStyles from './mapStyles.json';

const GOOGLEMAPSCRIPT = 'google-map-script';

const markersSVGProps = {
  default: {
    // eslint-disable-next-line max-len
    path: 'M2 14.0556C2 17.3269 3.29701 20.294 5.40261 22.466L12.6202 32.2999C12.9434 32.7401 13.4555 33 14 33C14.5445 33 15.0566 32.7401 15.3797 32.2999L22.5973 22.4661C24.703 20.2941 26 17.327 26 14.0556C26 7.39746 20.6274 2 14 2C7.37258 2 2 7.39746 2 14.0556Z',
    fillColor: 'white',
    fillOpacity: 1,
    strokeWeight: 2.5,
    rotation: 0,
    scale: 1,
    strokeColor: '#FF5F14',
  },
  hover: {
    // eslint-disable-next-line max-len
    path: 'M2.28577 16.0635C2.28577 19.8022 3.76807 23.1931 6.17447 25.6754L14.4232 36.9141C14.7925 37.4173 15.3778 37.7143 16 37.7143C16.6223 37.7143 17.2076 37.4173 17.5769 36.9141L25.8255 25.6755C28.232 23.1932 29.7143 19.8023 29.7143 16.0635C29.7143 8.45424 23.5742 2.28572 16.0001 2.28572C8.42586 2.28572 2.28577 8.45424 2.28577 16.0635Z',
    fillColor: '#FFEFE8',
    fillOpacity: 1,
    strokeWeight: 2.5,
    rotation: 0,
    scale: 1,
    strokeColor: '#FF5F14',
  },
  selected: {
    // eslint-disable-next-line max-len
    path: 'M2.28571 16.0635C2.28571 19.8022 3.76801 23.1931 6.17441 25.6754L14.4231 36.9141C14.7924 37.4173 15.3777 37.7143 16 37.7143C16.6223 37.7143 17.2075 37.4173 17.5768 36.9141L25.8255 25.6755C28.2319 23.1932 29.7143 19.8023 29.7143 16.0635C29.7143 8.45423 23.5742 2.28571 16 2.28571C8.4258 2.28571 2.28571 8.45423 2.28571 16.0635Z',
    fillColor: '#FF5F14',
    fillOpacity: 1,
    strokeWeight: 2.5,
    rotation: 0,
    scale: 1,
    strokeColor: '#FF5F14',
  },
};

const GoogleMap = ({
  mapid, markersList, selectedPoint, showAll,
}) => {
  const googleMapRef = useRef();
  const [map, setMap] = useState(null);

  const [markers, setMarkers] = useState([]);
  const [geocoder, setGeoCoder] = useState(null);
  const [mapIsReady, setMapIsReady] = useState(false);
  const [locations, setLocations] = useState(null);

  const createMarkers = (markersList) => {
    markers.forEach((marker) => marker.setMap(null));
    const bounds = new window.google.maps.LatLngBounds();

    const googleMarkers = markersList.map((item) => {
      const {
        latitude, longitude, onClick, selected,
      } = item;

      const marker = new window.google.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map,
        icon: {
          ...markersSVGProps[selected ? 'selected' : 'default'],
          anchor: new window.google.maps.Point(15, 30),
        },
      });
      marker.setZIndex((selected) ? 1 : 0);
      marker.addListener('click', onClick);
      marker.addListener('mouseover', () => {
        marker.setIcon({
          ...markersSVGProps[selected ? 'selected' : 'hover'],
          anchor: new window.google.maps.Point(15, 30),
        });
      });
      marker.addListener('mouseout', () => {
        marker.setIcon({
          ...markersSVGProps[selected ? 'selected' : 'default'],
          anchor: new window.google.maps.Point(15, 30),
        });
      });

      bounds.extend(marker.position);
      return marker;
    });

    return googleMarkers;
  };

  const createGoogleMap = () => {
    const latlng = new window.google.maps.LatLng(43.653200, -79.383200);
    return new window.google.maps.Map(googleMapRef.current, {
      zoom: 9,
      center: latlng,
      disableDefaultUI: true,
      controlSize: 24,
      mapTypeControl: false,
      mapTypeId: 'roadmap',
      scaleControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      scrollwheel: false,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      styles: mapStyles,
    });
  };

  const createGeoCoder = () => new window.google.maps.Geocoder();

  const getCoordsFromPostalCode = (idx, postalCode) => {
    if (geocoder) {
      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: postalCode }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
            const lat = results[0].geometry.location.lat();
            const long = results[0].geometry.location.lng();
            resolve({
              stts: 'ok', msg: '', lat, lng: long, postalCode, idx,
            });
          } else {
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({
              stts: 'error', msg: status, lat: null, lng: null, postalCode, idx,
            });
          }
        });
      });
    }

    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      stts: 'error', msg: 'Geocoder not initialized', lat: null, lng: null, postalCode, idx,
    });
  };

  const handleMarkersList = (markersList) => {
    const data = [];
    const isValidPostalCode = (postalCode) => {
      const canadaPostalRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
      const usaPostalRegex = /^\d{5}(?:[-\s]\d{4})?$/;
      return canadaPostalRegex.test(postalCode) || usaPostalRegex.test(postalCode);
    };

    markersList.forEach((item, index) => {
      const { latitude, longitude, postalCode } = item;
      const hasCoords = (latitude && longitude && (latitude !== 0 && longitude !== 0));

      if (!hasCoords && postalCode && isValidPostalCode(postalCode)) {
        data.push(getCoordsFromPostalCode(index, postalCode));
      }
    });

    const updatedMarkers = [...markersList];

    Promise.all(data).then((results) => {
      if (results) {
        results.forEach((result) => {
          const { idx, lat, lng } = result;
          updatedMarkers[idx].latitude = lat;
          updatedMarkers[idx].longitude = lng;
        });
      }

      const validMarkers = updatedMarkers.filter(
        (item) => item.latitude && item.longitude && (item.latitude !== 0 && item.longitude !== 0),
      );

      setLocations(validMarkers);
    });
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const gmscript = document.getElementById(GOOGLEMAPSCRIPT);

      const callback = () => {
        setMap(createGoogleMap());
        setGeoCoder(createGeoCoder());
      };

      if (!gmscript) {
        const googleMapScript = document.createElement('script');
        googleMapScript.src = googleMapUrl;
        googleMapScript.id = GOOGLEMAPSCRIPT;
        window.document.body.appendChild(googleMapScript);
        googleMapScript.addEventListener('load', callback);
      } else {
        callback();
      }
    }
  }, []);

  useEffect(() => {
    if (map && geocoder) {
      handleMarkersList(markersList);
    }
  }, [map, geocoder, markersList]);

  // Create markers after get coords of points with only postal code
  useEffect(() => {
    if (map && locations) {
      const mkrs = createMarkers(locations);
      setMarkers(mkrs);
      setMapIsReady(true);
    }
  }, [map, locations]);

  // Center map in a selected point position
  useEffect(() => {
    if (mapIsReady && selectedPoint) {
      const selectedObj = markersList.find((marker) => marker.id === selectedPoint);
      const { latitude, longitude } = selectedObj;

      const hasCoords = (latitude && longitude);

      if (hasCoords) {
        map.setCenter({ lat: latitude, lng: longitude });
      }
    }
  }, [selectedPoint, mapIsReady]);

  // Show all the markers on map
  useEffect(() => {
    if (map && mapIsReady && showAll && markers) {
      if (markersList.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();

        for (let i = 0; i < markersList.length; i++) {
          const { latitude, longitude } = markersList[i];
          if (latitude && longitude) {
            bounds.extend({ lat: latitude, lng: longitude });
          }
        }

        map.fitBounds(bounds);
      }

      if (markersList.length === 1) {
        const { latitude, longitude } = markersList[0];
        map.setCenter({ lat: latitude, lng: longitude });
      }
    }
  }, [showAll, mapIsReady]);

  return (
    <GoogleMapContainer
      id={mapid}
      className="googleMap"
      ref={googleMapRef}
    />
  );
};

GoogleMap.propTypes = {
  mapid: PropTypes.string,
  markersList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    postalCode: PropTypes.string,
    onClick: PropTypes.func,
    selected: PropTypes.bool,
  })),
  selectedPoint: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showAll: PropTypes.bool,
};

export default GoogleMap;
